import { useState } from 'react';

import { Alert, AlertType } from '../components/Alert';
import Card from '../components/Card';
import Container from '../components/Container';
import PageHeader from '../components/PageHeader';

import { useAccountUser } from '../contexts/User';
import { UserForm, UserFormState } from '../components/UserForm';
import EmailVerifyForm from '../components/EmailVerifyForm';

export default function UserSettings() {
  const { user, updateUser, verifyEmail, sendVerificationCode } = useAccountUser();
  const [error, setError] = useState<Error | undefined>(undefined);
  const [success, setSuccess] = useState<string | undefined>(undefined);

  if (!user) return null;

  const handleUserFormSubmit = async (values: UserFormState) => {
    setError(undefined);
    setSuccess(undefined);
    try {
      await updateUser(values.user);
      setSuccess('Your user setting was successfully updated.');
    } catch (e) {
      const err = e as Error;
      setError(err);
    }
  };

  const handleVerifyEmail = async (code: string) => {
    setError(undefined);
    setSuccess(undefined);
    try {
      await verifyEmail(code);
      setSuccess('Your email was successfully verified.');
    } catch (e) {
      const err = e as Error;
      setError(err);
    }
  };

  const handleResendCode = async () => {
    setError(undefined);
    setSuccess(undefined);
    try {
      await sendVerificationCode();
      setSuccess('Your verification code was resent.');
    } catch (e) {
      const err = e as Error;
      setError(err);
    }
  };

  return (
    <Container>
      <PageHeader title="User Settings" subtitle={`ID: ${user.email}`} />
      <Card className="p-12">
        {success && (
          <Alert
            className="mb-8"
            type={AlertType.SUCCESS}
            title="Success!"
            message={user.emailVerified ? success : `${success} Please verify your email.`}
          />
        )}
        {error && (
          <Alert
            className="mb-8"
            type={AlertType.ERROR}
            title="There was an error with your submission"
            message={error?.message || 'An unexpected error occurred.'}
          />
        )}
        {user.emailVerified ? (
          <UserForm handleSubmit={handleUserFormSubmit} />
        ) : (
          <EmailVerifyForm
            handleVerifyEmail={handleVerifyEmail}
            handleResendCode={handleResendCode}
          />
        )}
      </Card>
    </Container>
  );
}
